"use client";

import * as React from "react";
import { FileText, Route, ShieldCheck, Truck, Wallet } from "lucide-react";
import type { Viagem } from "@/types";
import { fmt } from "@/lib/format";
import { resolvePeriodo } from "@/lib/periodo";
import { STATUS_AVERBACAO, STATUS_CTE, STATUS_MDFE, STATUS_VIAGEM, statusCiot } from "@/lib/status";
import { StatusBadge } from "@/components/status";
import { Timeline } from "@/components/timeline";
import { useViagem, useViagemEventos, useViagens } from "@/features/viagens/api";
import { useViagemActions } from "@/features/viagens/components/viagem-actions";
import { useNav } from "../nav";
import { Card, Chips, KV, List, ListSkeleton, MEmpty, Page, Row, SearchField, Section, useInfinite } from "../ui";
import { CadeiaVertical, RotaHero, ViagemCard } from "../cards";
import { ActionFooter } from "../actions";

type Filtro = "todas" | "hoje" | "transito" | "erro" | "rascunho" | "encerrada";

function norm(s: string) {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

export function MViagens({ params }: { params: Record<string, string> }) {
  const [filtro, setFiltro] = React.useState<Filtro>((params.filtro as Filtro) ?? "todas");
  const [busca, setBusca] = React.useState("");
  const q = useViagens(resolvePeriodo("30d"));
  const hoje = resolvePeriodo("hoje").de;

  const contagem = React.useMemo(() => {
    const d = q.data ?? [];
    return {
      todas: d.length,
      hoje: d.filter((v) => v.data.slice(0, 10) >= (hoje ?? "")).length,
      transito: d.filter((v) => v.situacao === "emitida").length,
      erro: d.filter((v) => v.situacao === "erro").length,
      rascunho: d.filter((v) => v.situacao === "rascunho").length,
      encerrada: d.filter((v) => v.situacao === "encerrada").length,
    };
  }, [q.data, hoje]);

  const rows = React.useMemo(() => {
    let d = q.data ?? [];
    if (filtro === "hoje") d = d.filter((v) => v.data.slice(0, 10) >= (hoje ?? ""));
    if (filtro === "transito") d = d.filter((v) => v.situacao === "emitida");
    if (filtro === "erro") d = d.filter((v) => v.situacao === "erro");
    if (filtro === "rascunho") d = d.filter((v) => v.situacao === "rascunho");
    if (filtro === "encerrada") d = d.filter((v) => v.situacao === "encerrada");
    const b = norm(busca.trim());
    if (b) d = d.filter((v) => norm(`${v.numero} ${v.tomadorNome} ${v.placa} ${v.motoristaNome} ${v.origem.municipio} ${v.destino.municipio}`).includes(b));
    return d;
  }, [q.data, filtro, busca, hoje]);
  const { visiveis, sentinel } = useInfinite(q.data ? rows : undefined);

  return (
    <Page title="Viagens" subtitle={q.data ? `${fmt.int(rows.length)} nos últimos 30 dias` : undefined} onRefresh={() => q.refetch()}>
      <div className="sticky top-0 z-10 space-y-2.5 bg-app px-4 pt-3 pb-2">
        <SearchField value={busca} onChange={setBusca} placeholder="Número, tomador, placa, cidade" />
        <Chips
          value={filtro}
          onChange={setFiltro}
          options={[
            { value: "todas", label: "Todas", count: contagem.todas },
            { value: "hoje", label: "Hoje", count: contagem.hoje },
            { value: "transito", label: "Em trânsito", count: contagem.transito },
            { value: "erro", label: "Com erro", count: contagem.erro, tom: contagem.erro ? "danger" : undefined },
            { value: "rascunho", label: "Rascunhos", count: contagem.rascunho },
            { value: "encerrada", label: "Encerradas", count: contagem.encerrada },
          ]}
        />
      </div>
      <div className="flex flex-col gap-2.5 px-4 pt-1 pb-6">
        {!q.data ? (
          <ListSkeleton tall />
        ) : rows.length === 0 ? (
          <MEmpty icon={Route} text={busca ? "Nenhum resultado" : "Nenhuma viagem"} />
        ) : (
          visiveis?.map((v) => <ViagemCard key={v.id} v={v} onClick={() => useNav.getState().push("viagem", { id: v.id })} />)
        )}
        {sentinel}
      </div>
    </Page>
  );
}

/* ---------- Detalhe ---------- */

export function MViagem({ params }: { params: Record<string, string> }) {
  const q = useViagem(params.id);
  const v = q.data;
  const processando = v?.situacao === "processando";
  useViagem(processando ? params.id : null, { refetchInterval: processando ? 1500 : false });
  const ev = useViagemEventos(params.id);
  const acoes = useViagemActions(v, () => useNav.getState().back());

  return (
    <Page
      back
      title={v ? `Viagem ${fmt.int(v.numero)}` : "Viagem"}
      subtitle={v ? fmt.dateTime(v.data) : undefined}
      onRefresh={() => Promise.all([q.refetch(), ev.refetch()])}
      footer={v ? <ActionFooter acoes={acoes.toolbar} permitidas={["encerrar", "reenviar", "cancelar"]} /> : undefined}
    >
      {!v ? (
        <div className="p-4">
          <ListSkeleton rows={4} tall />
        </div>
      ) : (
        <ViagemConteudo v={v} eventos={ev.data} />
      )}
      {acoes.dialogs}
    </Page>
  );
}

function ViagemConteudo({ v, eventos }: { v: Viagem; eventos: ReturnType<typeof useViagemEventos>["data"] }) {
  const docs = v.documentos;
  const push = useNav.getState().push;
  return (
    <div className="pb-6">
      <Section>
        <Card className="p-4">
          <div className="flex items-start justify-between gap-3">
            <RotaHero origem={`${v.origem.municipio}/${v.origem.uf}`} destino={`${v.destino.municipio}/${v.destino.uf}`} />
            <StatusBadge def={STATUS_VIAGEM[v.situacao]} />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 border-t border-border pt-3">
            <div>
              <p className="text-xs text-muted">Frete</p>
              <p className="text-[15px] font-semibold tnum">{fmt.money(v.frete)}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Carga</p>
              <p className="truncate text-[15px] font-semibold tnum">{fmt.moneyShort(v.valorCarga)}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Peso</p>
              <p className="truncate text-[15px] font-semibold tnum">{fmt.dec(v.pesoKg / 1000)} t</p>
            </div>
          </div>
        </Card>
      </Section>

      {v.cadeia ? (
        <Section title="Cadeia">
          <CadeiaVertical cadeia={v.cadeia} />
        </Section>
      ) : null}

      <Section title="Partes">
        <KV
          items={[
            { label: "Tomador", value: v.tomadorNome },
            { label: "Remetente", value: v.dados.remetente?.nome },
            { label: "Destinatário", value: v.dados.destinatario?.nome },
          ]}
        />
      </Section>

      <Section title="Veículo">
        <KV
          items={[
            { label: "Placa", value: <span className="font-mono">{v.placa}</span> },
            { label: "Motorista", value: v.motoristaNome },
            { label: "Percurso", value: [v.origem.uf, ...v.dados.percurso, v.destino.uf].join(" → ") },
          ]}
        />
      </Section>

      {docs && (docs.ctes.length || docs.mdfe || docs.ciot) ? (
        <Section title="Documentos">
          <List>
            {docs.ctes.map((c) => (
              <Row key={c.id} icon={FileText} title={`CT-e ${fmt.int(c.numero)}`} subtitle={fmt.money(c.valor)} trailing={<StatusBadge def={STATUS_CTE[c.situacao]} />} onClick={() => push("cte", { id: c.id })} />
            ))}
            {docs.averbacoes.map((a) => (
              <Row key={a.id} icon={ShieldCheck} title={`Averbação CT-e ${fmt.int(a.cteNumero)}`} subtitle={a.averbadora} trailing={<StatusBadge def={STATUS_AVERBACAO[a.situacao]} />} />
            ))}
            {docs.ciot ? (
              <Row
                icon={Wallet}
                title={`CIOT ${docs.ciot.numero || "—"}`}
                subtitle={`${docs.ciot.ipef} · ${fmt.money(docs.ciot.valor)}`}
                trailing={<StatusBadge def={statusCiot(docs.ciot)} />}
                onClick={() => push("ciot", { id: docs.ciot!.id })}
              />
            ) : null}
            {docs.mdfe ? (
              <Row
                icon={Truck}
                title={`MDF-e ${fmt.int(docs.mdfe.numero)}`}
                subtitle={docs.mdfe.placa}
                trailing={<StatusBadge def={STATUS_MDFE[docs.mdfe.situacao]} />}
                onClick={() => push("mdfe", { id: docs.mdfe!.id })}
              />
            ) : null}
          </List>
        </Section>
      ) : null}

      <Section title="Atividade">
        <Card className="py-1">
          <Timeline eventos={eventos} compact />
        </Card>
      </Section>
    </div>
  );
}
